import React, { useContext, useState } from 'react';
import { Col, Row, Segmented, Tag, Typography } from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import { PipelineOpportunity } from '@totem/components/accountPlan/types';
import {
  money,
  moneyExact,
  shortDate,
  StatBox,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

type Filter = 'open' | 'won' | 'lost' | 'all';

const STAGE_TAG: Record<string, { color: string; label: string }> = {
  open: { color: 'blue', label: 'Open' },
  won: { color: 'green', label: 'Won' },
  lost: { color: 'default', label: 'Lost' },
};

const PipelineCard = () => {
  const { data } = useContext(AccountPlanContext);
  const [filter, setFilter] = useState<Filter>('open');
  if (!data) {
    return null;
  }
  const { pipeline } = data.summary;
  const rows = pipeline.opportunities.filter(
    (opportunity) => filter === 'all' || opportunity.stage === filter,
  );

  const columns = [
    {
      title: 'Opportunity',
      dataIndex: 'title',
      render: (title: string, opportunity: PipelineOpportunity) => (
        <>
          <Typography.Text strong>{title}</Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{opportunity.number} · {opportunity.salesRep}
            </Typography.Text>
          </div>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'stage',
      width: 90,
      render: (stage: string) => (
        <Tag color={STAGE_TAG[stage]?.color}>
          {STAGE_TAG[stage]?.label ?? stage}
        </Tag>
      ),
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      width: 100,
      align: 'right' as const,
      render: (probability: number) => `${probability}%`,
    },
    {
      title: 'Projected',
      dataIndex: 'projectedTotal',
      width: 120,
      align: 'right' as const,
      render: (total: number) => moneyExact(total),
    },
    {
      title: 'Weighted',
      dataIndex: 'weighted',
      width: 120,
      align: 'right' as const,
      render: (weighted: number, opportunity: PipelineOpportunity) =>
        opportunity.stage === 'open' ? moneyExact(weighted) : '—',
    },
    {
      title: 'Expected close',
      dataIndex: 'expectedClose',
      width: 140,
      render: (date: string | undefined, opportunity: PipelineOpportunity) =>
        opportunity.pastDue ? (
          <Tag color="gold">{shortDate(date)} · past due</Tag>
        ) : (
          shortDate(date)
        ),
    },
  ];

  return (
    <PlanCard
      variant="live"
      title="Pipeline"
      liveTip="NetSuite opportunities matched through the organization's NetSuite customer keys. Weighted = projected total × probability. Won/lost counts cover the last 12 months."
      extra={`${pipeline.openCount} open opportunities`}
    >
      <Row gutter={10} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <StatBox
            label="Open pipeline"
            value={money(pipeline.openTotal)}
            meta={`${pipeline.openCount} opportunities`}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatBox
            label="Weighted"
            value={money(pipeline.weightedTotal)}
            meta="by probability"
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatBox
            label="Past expected close"
            value={pipeline.pastDueCount}
            meta={
              pipeline.pastDueCount > 0
                ? `${money(pipeline.pastDueTotal)} to re-date or close`
                : 'all current'
            }
            alert={pipeline.pastDueCount > 0}
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatBox
            label="Won · last 12 months"
            value={money(pipeline.wonTotal)}
            meta={`${pipeline.wonCount} won · ${pipeline.lostCount} lost`}
          />
        </Col>
      </Row>
      <Segmented<Filter>
        style={{ marginBottom: 8 }}
        value={filter}
        onChange={setFilter}
        options={[
          { label: `Open (${pipeline.openCount})`, value: 'open' },
          { label: 'Won', value: 'won' },
          { label: 'Lost', value: 'lost' },
          { label: 'All', value: 'all' },
        ]}
      />
      <IBTable
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={rows}
        expandable={{
          rowExpandable: (opportunity: PipelineOpportunity) =>
            opportunity.lines.length > 0,
          expandedRowRender: (opportunity: PipelineOpportunity) => (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {opportunity.lines.map((line, index) => (
                <li key={index}>
                  {line.description || line.item} — {moneyExact(line.amount)}
                </li>
              ))}
            </ul>
          ),
        }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          hideOnSinglePage: true,
        }}
        locale={{
          emptyText: 'No NetSuite opportunities for this organization',
        }}
      />
    </PlanCard>
  );
};

export default PipelineCard;
